import React, { memo, useMemo } from 'react';
import {
    Box,
    InputAdornment,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import styled from '@mui/system/styled';
import Flex from 'ui/Flex/Flex';
import { parseNumericInput } from 'helpers/UIHelpers';
import NumberFormatCustom from 'components/NumberFormat/NumberFormatCustom';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import ApplicationDateInput from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/informationSelect/ApplicationDateInput';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { useGetPayComponentAmount } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/hooks/agentPayComponentHypothesis';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { MANTY_COLORS } from 'business/constants';
import _ from 'lodash';
import { FormState } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/AgentHypothesisModal';
import { useTranslation } from 'react-i18next';
import { FreeHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';

const StyPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    height: '100%',
}));
const StyBlock = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
    padding: '12px',
    gap: '20px',
    height: '100%',
}));

const AgentIfseHypothesisModalContent = ({
    hrSimulation,
    agentData,
    formState,
    setFormState,
    freeHypothesisAffectingAgent,
}: {
    hrSimulation: HrSimulation;
    agentData: SimulationAgentData;
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>;
    freeHypothesisAffectingAgent: FreeHypothesis | undefined;
}): React.ReactElement => {
    const { t } = useTranslation();
    const { data: ifseAmountData, isLoading: isIfseAmountLoading } =
        useGetPayComponentAmount({
            simulationId: hrSimulation.id,
            agent: {
                recurrence: RecurrenceEnum.RECURRENT,
                orgChartHrSoftwareId: agentData.orgChartHrSoftwareId,
                jobNumber: agentData.jobNumber,
                mantyStatus: agentData.mantyStatus,
                agentSoftwareId: agentData.agentSoftwareId,
            },
            payComponent: PayComponent.IFSE_AND_MONTHLY_PRIMES,
        });

    const ifseAmounts = useMemo(() => ifseAmountData ?? [], [ifseAmountData]);
    const { ifseStartDate: ifseStartDateForm } = formState;
    const ifseStartDate =
        freeHypothesisAffectingAgent?.filters?.date ?? ifseStartDateForm;

    const onStartDateChange = (date: SimulationDateString) => {
        setFormState((prevState) => ({
            ...prevState,
            ifseStartDate: date,
        }));
    };
    return (
        <StyPaper>
            <StyBlock>
                <Typography variant="body1Bold">
                    {t(
                        'humanResources.simulation.freeHypothesis.ifse.applyValues',
                    )}
                </Typography>
                <ApplicationDateInput
                    hrSimulation={hrSimulation}
                    applicationDate={ifseStartDate}
                    setApplicationDate={onStartDateChange}
                    restrictMonth
                />
                <Flex flexDirection="row" gap="10px">
                    {isIfseAmountLoading ? (
                        <Skeleton sx={{ flex: 1 }} />
                    ) : (
                        <TextField
                            variant="outlined"
                            value={
                                ifseAmounts.find(
                                    (a) => a.date === ifseStartDate,
                                )?.amount ?? 0
                            }
                            label={t(
                                freeHypothesisAffectingAgent
                                    ? 'humanResources.simulation.currentSimulatedValue'
                                    : 'humanResources.simulation.currentValue',
                            )}
                            sx={{ flex: 1, backgroundColor: 'white' }}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        €
                                    </InputAdornment>
                                ),
                            }}
                            disabled
                        />
                    )}

                    <TextField
                        variant="outlined"
                        value={formState.newIfseValue}
                        label={t(
                            'humanResources.simulation.freeHypothesis.ifse.newValue',
                        )}
                        onChange={(event) => {
                            const value = parseNumericInput(event.target.value);

                            setFormState({
                                ...formState,
                                newIfseValue: value || 0,
                                hasIfseChanges: true,
                            });
                        }}
                        sx={{ flex: 1, backgroundColor: MANTY_COLORS.white }}
                        InputProps={{
                            inputComponent: NumberFormatCustom,
                            startAdornment: (
                                <InputAdornment position="start">
                                    €
                                </InputAdornment>
                            ),
                        }}
                        required
                    />
                </Flex>
            </StyBlock>
        </StyPaper>
    );
};

export default memo(AgentIfseHypothesisModalContent, _.isEqual);
